import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAccount,
  createTransaction,
  deleteTransaction,
  createGoal,
  deleteGoal,
} from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import AnimatedNumber from "@/components/AnimatedNumber";
import GoalTimeline from "@/components/GoalTimeline";
import AmountEntryModal from "@/components/AmountEntryModal";
import TransactionFeed from "@/components/TransactionFeed";
import AddGoalForm from "@/components/AddGoalForm";
import ParentSettingsSheet from "@/components/ParentSettingsSheet";
import { useConfetti } from "@/components/Confetti";
import { toast } from "sonner";
import { Sparkles, Coins, Settings, Users } from "lucide-react";

export default function KidsMoneyApp() {
  const { activeKidId, selectKid } = useAuth();
  const [kid, setKid] = useState(null);
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { fire, ConfettiLayer } = useConfetti();

  const reloadAccount = async () => {
    try {
      const data = await fetchAccount(activeKidId);
      setKid(data.kid);
      setBalance(data.balance);
      setTransactions(data.transactions || []);
      setGoals(data.goals || []);
    } catch (e) {
      if (e.response?.status === 404) {
        selectKid(null);
        return;
      }
      console.error(e);
      toast.error("Couldn't load your money. Try again!");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reloadAccount();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKidId]);

  const checkGoalsReached = (oldBalance, newBalance) => {
    const newlyReached = goals.filter(
      (g) => g.price > oldBalance && g.price <= newBalance
    );
    if (newlyReached.length > 0) {
      fire(60);
      const g = newlyReached[0];
      setTimeout(() => {
        toast.success(`🎉 You can buy ${g.name}!`, { duration: 4000 });
      }, 200);
    }
  };

  const handleConfirmTransaction = async ({ type, amount, description, tag }) => {
    try {
      const oldBalance = balance;
      const tx = await createTransaction(activeKidId, { type, amount, description, tag });

      const newBalance = type === "make" ? oldBalance + amount : oldBalance - amount;
      setBalance(newBalance);
      setTransactions((prev) => [tx, ...prev]);

      if (type === "make") {
        fire(40);
        toast.success(`Nice! +$${amount.toLocaleString()}`, { duration: 2500 });
        checkGoalsReached(oldBalance, newBalance);
      } else {
        const normalized = (description || "").trim().toLowerCase();
        const matchedGoal = normalized
          ? goals.find((g) => g.name.trim().toLowerCase() === normalized)
          : null;

        if (matchedGoal) {
          try {
            await deleteGoal(activeKidId, matchedGoal.id);
            setGoals((prev) => prev.filter((g) => g.id !== matchedGoal.id));
            fire(50);
            toast.success(`You bought ${matchedGoal.name}! 🎉 Crossing it off!`, {
              duration: 3500,
            });
          } catch (err) {
            console.error("Failed to auto-remove goal:", err);
            toast(`Spent $${amount.toLocaleString()} on ${description}`, {
              duration: 2500,
            });
          }
        } else {
          toast(`Spent $${amount.toLocaleString()}${description ? ` on ${description}` : ""}`, {
            duration: 2500,
          });
        }
      }
      setModalMode(null);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save that. Try again!");
    }
  };

  const handleAddGoal = async ({ name, price }) => {
    try {
      const goal = await createGoal(activeKidId, { name, price });
      setGoals((prev) => [...prev, goal]);
      toast.success(`Added ${name}! 🎯`, { duration: 2000 });
      if (balance >= price) fire(30);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't add goal. Try again!");
    }
  };

  const handleDeleteGoal = async (goalId, name) => {
    const ok = window.confirm(`Remove "${name}" from your goals?`);
    if (!ok) return;
    try {
      await deleteGoal(activeKidId, goalId);
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      toast(`Removed ${name}`, { duration: 1800 });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't delete goal. Try again!");
    }
  };

  const handleDeleteTransaction = async (id) => {
    const ok = window.confirm("Delete this transaction? Your balance will be fixed.");
    if (!ok) return;
    try {
      const tx = transactions.find((t) => t.id === id);
      await deleteTransaction(activeKidId, id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
      if (tx) {
        const delta = tx.type === "make" ? -tx.amount : tx.amount;
        setBalance((b) => b + delta);
      }
      toast("Transaction removed", { duration: 1500 });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't delete. Try again!");
    }
  };

  const firstName = useMemo(() => kid?.name || "", [kid]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="chunky-card p-8 text-center" style={{ backgroundColor: "#FFC400" }}>
          <div className="font-display text-3xl text-navy">Loading...</div>
          <div className="font-display text-base text-navy/70 mt-1">
            Counting your money 🪙
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="kids-money-app" className="min-h-screen pb-12">
      <ConfettiLayer />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-5 sm:pt-7">
        {/* Header */}
        <header data-testid="app-header" className="relative flex flex-col items-center mb-4">
          <div className="absolute right-0 top-0 flex gap-2">
            <button
              data-testid="switch-kid-btn"
              onClick={() => selectKid(null)}
              className="w-11 h-11 rounded-full border-4 border-navy bg-white flex items-center justify-center hover:-translate-y-0.5 transition-transform"
              style={{ boxShadow: "0 4px 0 0 #2A2D7C" }}
              aria-label="Switch kid"
            >
              <Users className="w-5 h-5 text-navy" strokeWidth={3} />
            </button>
            <button
              data-testid="parent-settings-btn"
              onClick={() => setSettingsOpen(true)}
              className="w-11 h-11 rounded-full border-4 border-navy bg-white flex items-center justify-center hover:-translate-y-0.5 transition-transform"
              style={{ boxShadow: "0 4px 0 0 #2A2D7C" }}
              aria-label="Parent settings"
            >
              <Settings className="w-5 h-5 text-navy" strokeWidth={3} />
            </button>
          </div>

          <img
            src={LOGO_URL}
            alt="KIDS MONEY"
            className="h-28 sm:h-36 w-auto object-contain"
          />
          <div
            data-testid="kid-name-heading"
            className="font-display text-3xl sm:text-4xl text-navy -mt-1"
          >
            {firstName.toUpperCase()}
          </div>
        </header>

        {/* Big total */}
        <div
          data-testid="balance-card"
          className="chunky-card relative overflow-hidden text-center p-6 sm:p-8"
          style={{ backgroundColor: "#29C7E8" }}
        >
          <div className="absolute -top-6 -right-6 opacity-20">
            <Coins className="w-32 h-32 text-navy" strokeWidth={2} />
          </div>
          <div className="absolute -bottom-4 -left-4 opacity-15">
            <Sparkles className="w-24 h-24 text-navy" strokeWidth={2} />
          </div>

          <div className="relative">
            <div className="font-display text-base sm:text-lg text-navy/80 uppercase tracking-wide">
              You have
            </div>
            <div className="font-display text-7xl sm:text-8xl text-navy leading-none mt-1">
              <AnimatedNumber value={balance} testId="balance-amount" />
            </div>
            <div className="font-display text-sm sm:text-base text-navy/70 mt-2">
              Keep stacking it up! 🪙
            </div>
          </div>
        </div>

        {/* MAKE / SPEND buttons */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-5">
          <button
            data-testid="make-money-btn"
            onClick={() => setModalMode("make")}
            className="chunky-btn py-6 sm:py-7 text-2xl sm:text-3xl text-navy flex flex-col items-center gap-1"
            style={{ backgroundColor: "#7CE645" }}
          >
            <span className="text-3xl sm:text-4xl">+</span>
            <span>MAKE</span>
            <span className="text-base sm:text-lg font-display opacity-80">MONEY</span>
          </button>
          <button
            data-testid="spend-money-btn"
            onClick={() => setModalMode("spend")}
            className="chunky-btn py-6 sm:py-7 text-2xl sm:text-3xl text-white flex flex-col items-center gap-1"
            style={{ backgroundColor: "#FF5A36" }}
          >
            <span className="text-3xl sm:text-4xl">−</span>
            <span>SPEND</span>
            <span className="text-base sm:text-lg font-display opacity-90">MONEY</span>
          </button>
        </div>

        <GoalTimeline goals={goals} balance={balance} onDeleteGoal={handleDeleteGoal} />

        <AddGoalForm onAdd={handleAddGoal} />

        <TransactionFeed transactions={transactions} onDelete={handleDeleteTransaction} />

        <div className="mt-8 text-center font-display text-sm text-navy/50">
          made for {firstName} • keep stacking 💰
        </div>
      </div>

      <AmountEntryModal
        open={modalMode !== null}
        mode={modalMode}
        onClose={() => setModalMode(null)}
        onConfirm={handleConfirmTransaction}
      />

      <ParentSettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onKidRenamed={reloadAccount}
      />
    </div>
  );
}
